import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { isDirtyEqual } from 'utils/compares';

const saveFailureMessage = 'Unable to save changes. Please try again.';
const refreshFailureMessage =
  'Changes were saved, but the latest contract information could not be refreshed. Reload the page to confirm the latest values.';

export type ContractInformationSynchronizationIssueKind =
  | 'unverified'
  | 'unresolved'
  | 'correction-required';

export interface ContractInformationSynchronizationIssue {
  kind: ContractInformationSynchronizationIssueKind;
  message: string;
  details: readonly string[];
}

export type ContractInformationSynchronizationStatus =
  | 'ensuring'
  | 'ready'
  | ContractInformationSynchronizationIssueKind;

export interface ContractInformationSynchronizationResult {
  status: Exclude<ContractInformationSynchronizationStatus, 'ensuring'>;
}

export interface ContractInformationSynchronizationPolicy {
  ensure: () => Promise<ContractInformationSynchronizationResult>;
  retry: () => Promise<ContractInformationSynchronizationResult>;
  status: ContractInformationSynchronizationStatus;
  issue?: ContractInformationSynchronizationIssue;
}

export interface ContractInformationSaveAttempt<TDraft> {
  baseline: Readonly<TDraft>;
  draft: Readonly<TDraft>;
}

export type ContractInformationSaveResult<TDraft> =
  | { accepted: true; baseline?: TDraft }
  | { accepted: false; errors: readonly string[] };

interface ContractInformationFeedback {
  type: 'success' | 'warning';
  message: string;
}

type EditorSession = object;

interface ContractInformationRefreshPolicy {
  authority: 'mutation' | 'source';
  refreshSource: () => Promise<void>;
  refreshDependencies?: readonly (() => Promise<void>)[];
}

interface SaveOperation<TDraft extends object, TConfirmation> {
  session: EditorSession;
  attempt: {
    baseline: TDraft;
    draft: TDraft;
  };
  persist: UseContractInformationEditorOptions<TDraft, TConfirmation>['persist'];
  preflight: UseContractInformationEditorOptions<TDraft, TConfirmation>['preflight'];
  refresh: ContractInformationRefreshPolicy;
  synchronization?: ContractInformationSynchronizationPolicy;
  equal: NonNullable<UseContractInformationEditorOptions<TDraft, TConfirmation>['equal']>;
}

interface EditorState<TDraft extends object, TConfirmation> {
  resourceKey: string;
  session: EditorSession;
  baseline?: TDraft;
  draft?: TDraft;
  lastSource?: TDraft;
  phase:
    | 'loading'
    | 'ready'
    | 'ensuring'
    | 'preflighting'
    | 'awaiting-confirmation'
    | 'saving'
    | 'syncing';
  confirmation?: {
    details: TConfirmation;
    operation: SaveOperation<TDraft, TConfirmation>;
  };
  errors: readonly string[];
  feedback?: ContractInformationFeedback;
  warnOnSourceError: boolean;
  postSaveRecoveryPending: boolean;
}

export interface UseContractInformationEditorOptions<TDraft extends object, TConfirmation = never> {
  resourceKey: string;
  source: TDraft | undefined;
  isPending: boolean;
  isError: boolean;
  persist: (
    attempt: ContractInformationSaveAttempt<TDraft>
  ) => Promise<ContractInformationSaveResult<TDraft>>;
  preflight?: (
    attempt: ContractInformationSaveAttempt<TDraft>
  ) => Promise<TConfirmation | undefined>;
  refresh: ContractInformationRefreshPolicy;
  synchronization?: ContractInformationSynchronizationPolicy;
  equal?: (left: unknown, right: unknown) => boolean;
}

interface UpdateContractInformationDraft<TDraft> {
  <TField extends keyof TDraft>(field: TField, value: TDraft[TField]): void;
  (update: (current: Readonly<TDraft>) => TDraft): void;
}

export interface ContractInformationEditor<TDraft, TConfirmation> {
  draft: TDraft | undefined;
  isLoading: boolean;
  loadFailed: boolean;
  dirty: boolean;
  saving: boolean;
  checkingTaskSynchronization: boolean;
  synchronizationIssue: ContractInformationSynchronizationIssue | undefined;
  confirmation: TConfirmation | undefined;
  errors: readonly string[];
  feedback: ContractInformationFeedback | undefined;
  update: UpdateContractInformationDraft<TDraft>;
  save: () => Promise<void>;
  retryTaskSynchronization: () => Promise<void>;
  confirmSave: () => Promise<void>;
  cancelSave: () => void;
  dismissErrors: () => void;
}

const reconcileDraft = <TDraft extends object>(
  previousBaseline: TDraft,
  currentDraft: TDraft,
  incomingBaseline: TDraft,
  equal: (left: unknown, right: unknown) => boolean
): TDraft => {
  const draft = Object.assign({}, currentDraft);

  for (const key of Object.keys(incomingBaseline)) {
    const field = key as keyof TDraft;
    if (equal(currentDraft[field], previousBaseline[field])) {
      draft[field] = incomingBaseline[field];
    }
  }

  return draft;
};

const canContinueAfterEnsure = (result: ContractInformationSynchronizationResult) =>
  result.status === 'ready' || result.status === 'correction-required';

export const useContractInformationEditor = <TDraft extends object, TConfirmation = never>({
  resourceKey,
  source,
  isPending,
  isError,
  persist,
  preflight,
  refresh,
  synchronization,
  equal = isDirtyEqual,
}: UseContractInformationEditorOptions<TDraft, TConfirmation>): ContractInformationEditor<
  TDraft,
  TConfirmation
> => {
  const [state, setState] = useState<EditorState<TDraft, TConfirmation>>({
    resourceKey,
    session: {},
    phase: 'loading',
    errors: [],
    warnOnSourceError: false,
    postSaveRecoveryPending: false,
  });
  const stateRef = useRef(state);
  const optionsRef = useRef({ persist, preflight, refresh, synchronization, equal });
  const latestAcceptedSave = useRef(0);
  const mounted = useRef(true);

  useEffect(() => {
    optionsRef.current = { persist, preflight, refresh, synchronization, equal };
  }, [equal, persist, preflight, refresh, synchronization]);

  const updateState = (
    update: (current: EditorState<TDraft, TConfirmation>) => EditorState<TDraft, TConfirmation>
  ) => {
    setState((current) => {
      const next = update(current);
      stateRef.current = next;
      return next;
    });
  };

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useLayoutEffect(() => {
    if (stateRef.current.resourceKey === resourceKey) return;

    const next: EditorState<TDraft, TConfirmation> = {
      resourceKey,
      session: {},
      phase: 'loading',
      errors: [],
      warnOnSourceError: false,
      postSaveRecoveryPending: false,
    };
    stateRef.current = next;
    setState(next);
  }, [resourceKey]);

  useEffect(() => {
    if (!source || stateRef.current.resourceKey !== resourceKey) return;

    updateState((current) => {
      if (!current.baseline || !current.draft) {
        return {
          ...current,
          baseline: source,
          draft: source,
          lastSource: source,
          phase: 'ready',
        };
      }

      if (current.lastSource && equal(current.lastSource, source)) return current;

      return {
        ...current,
        baseline: source,
        draft: reconcileDraft(current.baseline, current.draft, source, equal),
        lastSource: source,
        feedback:
          current.feedback?.type === 'warning' && current.warnOnSourceError
            ? undefined
            : current.feedback,
        warnOnSourceError: false,
      };
    });
  }, [equal, resourceKey, source]);

  useEffect(() => {
    if (!isError) return;

    updateState((current) => {
      if (current.resourceKey !== resourceKey || !current.baseline || !current.warnOnSourceError)
        return current;

      return {
        ...current,
        feedback: {
          type: 'warning',
          message: refreshFailureMessage,
        },
      };
    });
  }, [isError, resourceKey]);

  useEffect(() => {
    if (synchronization?.status !== 'ready') return;

    updateState((current) => {
      if (!current.postSaveRecoveryPending || current.resourceKey !== resourceKey) return current;

      return {
        ...current,
        postSaveRecoveryPending: false,
        feedback: {
          type: 'success',
          message: 'Related tasks updated',
        },
      };
    });
  }, [resourceKey, synchronization?.status]);

  const currentState = state.resourceKey === resourceKey ? state : undefined;
  const renderSession = currentState?.session;

  function update<TField extends keyof TDraft>(field: TField, value: TDraft[TField]): void;
  function update(updateDraft: (current: Readonly<TDraft>) => TDraft): void;
  function update<TField extends keyof TDraft>(
    fieldOrUpdate: TField | ((current: Readonly<TDraft>) => TDraft),
    value?: TDraft[TField]
  ) {
    updateState((current) => {
      if (!current.draft || current.session !== renderSession) return current;

      return {
        ...current,
        draft:
          typeof fieldOrUpdate === 'function'
            ? fieldOrUpdate(current.draft)
            : Object.assign({}, current.draft, { [fieldOrUpdate]: value }),
        errors: [],
        feedback: undefined,
      };
    });
  }

  const isActive = (operation: Pick<SaveOperation<TDraft, TConfirmation>, 'session'>) =>
    mounted.current && stateRef.current.session === operation.session;

  const refreshAcceptedSave = async (
    operation: Pick<SaveOperation<TDraft, TConfirmation>, 'session' | 'refresh'>,
    saveNumber?: number
  ) => {
    const refreshes = [
      operation.refresh.refreshSource,
      ...(operation.refresh.refreshDependencies ?? []),
    ].map((refreshResource) => Promise.resolve().then(refreshResource));
    const [sourceRefresh] = await Promise.allSettled(refreshes);

    if (
      operation.refresh.authority !== 'source' ||
      !isActive(operation) ||
      (saveNumber !== undefined && latestAcceptedSave.current !== saveNumber)
    )
      return;

    if (sourceRefresh.status !== 'rejected') return;

    updateState((current) => ({
      ...current,
      feedback: {
        type: 'warning',
        message: refreshFailureMessage,
      },
    }));
  };

  const ensureBeforeSave = async (operation: SaveOperation<TDraft, TConfirmation>) => {
    if (!operation.synchronization) return true;

    updateState((current) => ({ ...current, phase: 'ensuring', errors: [] }));
    const result = await operation.synchronization.ensure();
    if (!isActive(operation)) return false;

    if (!canContinueAfterEnsure(result)) {
      updateState((current) => ({ ...current, phase: 'ready' }));
      return false;
    }

    if (stateRef.current.postSaveRecoveryPending && result.status === 'ready') {
      updateState((current) => ({
        ...current,
        postSaveRecoveryPending: false,
        feedback: {
          type: 'success',
          message: 'Related tasks updated',
        },
      }));
    }

    return true;
  };

  const persistOperation = async (operation: SaveOperation<TDraft, TConfirmation>) => {
    if (!isActive(operation)) return;

    updateState((current) => ({
      ...current,
      phase: 'saving',
      confirmation: undefined,
      errors: [],
      feedback: undefined,
    }));

    let result: ContractInformationSaveResult<TDraft>;

    try {
      result = await operation.persist(operation.attempt);
    } catch {
      if (!isActive(operation)) return;

      updateState((current) => ({
        ...current,
        phase: 'ready',
        errors: [saveFailureMessage],
      }));
      return;
    }

    if (!isActive(operation)) return;

    if (!result.accepted) {
      updateState((current) => ({
        ...current,
        phase: 'ready',
        errors: result.errors,
      }));
      return;
    }

    const savedDraft = result.baseline ?? operation.attempt.draft;
    const saveNumber = ++latestAcceptedSave.current;

    updateState((current) => ({
      ...current,
      baseline: savedDraft,
      draft: current.draft
        ? reconcileDraft(operation.attempt.draft, current.draft, savedDraft, operation.equal)
        : savedDraft,
      phase: operation.synchronization ? 'syncing' : 'ready',
      feedback: operation.synchronization
        ? undefined
        : {
            type: 'success',
            message: 'Changes saved successfully',
          },
      warnOnSourceError: !operation.synchronization && operation.refresh.authority === 'source',
    }));

    if (!operation.synchronization) {
      void refreshAcceptedSave(operation, saveNumber);
      return;
    }

    const synchronizationResult = await operation.synchronization.ensure();
    if (!isActive(operation)) return;

    updateState((current) => ({
      ...current,
      phase: 'ready',
      postSaveRecoveryPending: synchronizationResult.status !== 'ready',
      feedback:
        synchronizationResult.status === 'ready'
          ? {
              type: 'success',
              message: 'Changes saved successfully',
            }
          : undefined,
      warnOnSourceError:
        synchronizationResult.status === 'ready' && operation.refresh.authority === 'source',
    }));

    void refreshAcceptedSave(operation, saveNumber);
  };

  const save = async () => {
    const current = stateRef.current;
    if (
      current.phase !== 'ready' ||
      !current.baseline ||
      !current.draft ||
      optionsRef.current.synchronization?.status === 'ensuring' ||
      optionsRef.current.equal(current.baseline, current.draft)
    )
      return;

    const operation: SaveOperation<TDraft, TConfirmation> = {
      session: current.session,
      attempt: {
        baseline: current.baseline,
        draft: current.draft,
      },
      ...optionsRef.current,
    };

    if (operation.synchronization && !(await ensureBeforeSave(operation))) return;

    if (operation.preflight) {
      updateState((editorState) => ({ ...editorState, phase: 'preflighting', errors: [] }));

      try {
        const confirmation = await operation.preflight(operation.attempt);
        if (!isActive(operation)) return;

        if (confirmation !== undefined) {
          updateState((editorState) => ({
            ...editorState,
            phase: 'awaiting-confirmation',
            confirmation: { details: confirmation, operation },
          }));
          return;
        }
      } catch {
        if (!isActive(operation)) return;

        updateState((editorState) => ({
          ...editorState,
          phase: 'ready',
          errors: [saveFailureMessage],
        }));
        return;
      }
    }

    await persistOperation(operation);
  };

  const retryTaskSynchronization = async () => {
    const currentSynchronization = optionsRef.current.synchronization;
    if (!currentSynchronization || currentSynchronization.status === 'ensuring') return;

    await currentSynchronization.retry();
  };

  const confirmSave = async () => {
    const operation = stateRef.current.confirmation?.operation;
    if (!operation || !isActive(operation)) return;

    await persistOperation(operation);
  };

  const cancelSave = () => {
    updateState((current) =>
      current.session === renderSession
        ? { ...current, phase: 'ready', confirmation: undefined }
        : current
    );
  };

  const dismissErrors = () => {
    updateState((current) =>
      current.session === renderSession ? { ...current, errors: [] } : current
    );
  };

  const initialized = Boolean(currentState?.baseline && currentState.draft);
  const dirty = Boolean(
    currentState?.baseline &&
    currentState.draft &&
    !equal(currentState.baseline, currentState.draft)
  );
  const activeSave =
    currentState?.phase === 'ensuring' ||
    currentState?.phase === 'saving' ||
    currentState?.phase === 'preflighting' ||
    currentState?.phase === 'awaiting-confirmation' ||
    currentState?.phase === 'syncing';

  return {
    draft: currentState?.draft,
    isLoading: isPending && !initialized,
    loadFailed: isError && !initialized,
    dirty,
    saving: activeSave,
    checkingTaskSynchronization:
      currentState?.phase === 'ensuring' ||
      currentState?.phase === 'syncing' ||
      synchronization?.status === 'ensuring',
    synchronizationIssue: synchronization?.issue,
    confirmation: currentState?.confirmation?.details,
    errors: currentState?.errors ?? [],
    feedback: currentState?.feedback,
    update,
    save,
    retryTaskSynchronization,
    confirmSave,
    cancelSave,
    dismissErrors,
  };
};
